import { useState, useEffect, useCallback } from 'react';

export default function ConversionPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [hasShown, setHasShown] = useState(false);

  const showPopup = useCallback(() => {
    if (hasShown) return;
    if (sessionStorage.getItem('popup_shown')) return;
    setIsVisible(true);
    setHasShown(true);
    sessionStorage.setItem('popup_shown', 'true');
  }, [hasShown]);

  useEffect(() => {
    // 1) Exit intent (mouse leaves viewport top)
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0) showPopup();
    };
    document.addEventListener('mouseleave', handleMouseLeave);

    // 2) Time on page (25 seconds)
    const timer = setTimeout(showPopup, 25000);

    // 3) Scroll 50%
    const handleScroll = () => {
      const scrollPercent = window.scrollY / (document.body.scrollHeight - window.innerHeight);
      if (scrollPercent >= 0.5) showPopup();
    };
    window.addEventListener('scroll', handleScroll);

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [showPopup]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-fade-in"
        onClick={() => setIsVisible(false)}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-gray-800 border border-gray-700 rounded-2xl shadow-2xl shadow-yellow-500/10 animate-scale-in overflow-hidden">
        {/* Close */}
        <button
          onClick={() => setIsVisible(false)}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-gray-700/80 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-600 transition-colors z-10"
          aria-label="Fechar"
        >
          <i className="ri-close-line text-lg"></i>
        </button>

        {/* Top accent bar */}
        <div className="h-1.5 bg-gradient-to-r from-yellow-400 via-amber-500 to-orange-500" />

        <div className="p-6 sm:p-8 text-center">
          {/* Emoji / Icon */}
          <div className="text-5xl mb-4">🚀</div>

          {/* Headline */}
          <h3 className="text-2xl sm:text-3xl font-extrabold text-white mb-2 leading-tight">
            Espera! Não saia ainda...
          </h3>

          {/* Subheadline */}
          <p className="text-gray-300 mb-6 text-sm sm:text-base leading-relaxed">
            Crie sua conta <span className="text-yellow-400 font-bold">100% grátis</span> e comece a gerar recursos de multas em minutos. Sem compromisso!
          </p>

          {/* Benefits */}
          <div className="flex flex-col gap-2 mb-6 text-left">
            {[
              'Acesso imediato à plataforma',
              'Sem cartão de crédito necessário',
              'Cancele quando quiser',
            ].map((item) => (
              <div key={item} className="flex items-center gap-2 text-sm text-gray-300">
                <i className="ri-check-line text-yellow-400 text-base flex-shrink-0"></i>
                <span>{item}</span>
              </div>
            ))}
          </div>

          {/* CTA */}
          <a
            href="https://app.centraldamulta.app.br/register"
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full py-3.5 px-6 bg-gradient-to-r from-yellow-500 to-amber-500 text-gray-900 font-bold text-base sm:text-lg rounded-xl shadow-lg shadow-yellow-500/30 hover:shadow-xl hover:shadow-yellow-500/50 hover:scale-[1.02] transition-all duration-300 btn-shine"
          >
            Cadastre-se Grátis Agora
          </a>

          {/* Trust line */}
          <p className="text-gray-500 text-xs mt-4">
            +500 parceiros já utilizam a plataforma
          </p>
        </div>
      </div>

      <style>{`
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scale-in-popup { from { opacity: 0; transform: scale(0.9) translateY(20px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        .animate-fade-in { animation: fade-in 0.3s ease-out; }
        .animate-scale-in { animation: scale-in-popup 0.4s cubic-bezier(0.16, 1, 0.3, 1); }
      `}</style>
    </div>
  );
}
